import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { queryAccountSubmissionSchema } from "@utils/index";
import {
  QueryScopeType,
  DropdownItemModel,
  QueryAccountSubmissionFormInterface,
} from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import {
  useQueryGetTopics,
  useSubmissionGetAll,
  useQueryAccountSubmission,
} from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Form,
  FormRow,
  FormText,
  FormInput,
  FormCheckbox,
  Button,
  Dropdown,
} from "@components/index";

export const NewQueryAccountSubmission = ({
  businessId,
  startDate,
  endDate,
  category,
  presetTopicId,
  callRequested,
  onBack,
  onClose,
}: {
  businessId?: string;
  startDate?: string;
  endDate?: string;
  category: QueryScopeType;
  presetTopicId?: string;
  callRequested: boolean;
  onBack?: () => void;
  onClose: () => void;
}) => {
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);

  const [topics, setTopics] = useState<DropdownItemModel[]>([]);
  const [submissions, setSubmissions] = useState<DropdownItemModel[]>([]);

  const {
    isPending: isPendingGetTopics,
    isSuccess: isSuccessGetTopics,
    isStale: isStaleGetTopics,
    data: dataGetTopics,
    isError: isErrorGetTopics,
    error: errorGetTopics,
  } = useQueryGetTopics();

  useEffect(() => {
    if (isSuccessGetTopics && dataGetTopics) {
      setTopics(dataGetTopics);
    }
  }, [isSuccessGetTopics, dataGetTopics]);

  useEffect(() => {
    if (isErrorGetTopics && errorGetTopics) {
      notify("error", errorGetTopics.message);
    }
  }, [isErrorGetTopics, errorGetTopics, notify]);

  const {
    isPending: isPendingGetSubmissions,
    isSuccess: isSuccessGetSubmissions,
    isStale: isStaleGetSubmissions,
    data: dataGetSubmissions,
    isError: isErrorGetSubmissions,
    error: errorGetSubmissions,
  } = useSubmissionGetAll({
    businessId,
    startDate,
    endDate,
  });

  useEffect(() => {
    if (isSuccessGetSubmissions && dataGetSubmissions) {
      setSubmissions(
        dataGetSubmissions.submissions.map(({ uniqueId, period }) => ({
          id: uniqueId,
          title: period.title,
        })),
      );
    }
  }, [isSuccessGetSubmissions, dataGetSubmissions]);

  useEffect(() => {
    if (isErrorGetSubmissions && errorGetSubmissions) {
      notify("error", errorGetSubmissions.message);
    }
  }, [isErrorGetSubmissions, errorGetSubmissions, notify]);

  const {
    mutate: mutateQueryAccountSubmission,
    isPending: isPendingQueryAccountSubmission,
  } = useQueryAccountSubmission(
    () => {
      reset();
      onClose();
      notify("success", "Your message has been submitted.");
      setState({ isLoading: false });
    },
    ({ message, fields }) => {
      Object.keys(fields || {}).forEach((key) =>
        setError(
          key as
            | "category"
            | "topicId"
            | "subject"
            | "message"
            | "callRequested",
          {
            message: fields![key],
          },
        ),
      );
      notify("error", message);
      setState({ isLoading: false });
    },
  );

  useEffect(() => {
    setState({
      isLoading:
        (isStaleGetTopics && isPendingGetTopics) ||
        (isStaleGetSubmissions && isPendingGetSubmissions) ||
        isPendingQueryAccountSubmission,
    });
  }, [
    isStaleGetTopics,
    isPendingGetTopics,
    isStaleGetSubmissions,
    isPendingGetSubmissions,
    isPendingQueryAccountSubmission,
    setState,
  ]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setValue,
    getValues,
    watch,
    setError,
  } = useForm<QueryAccountSubmissionFormInterface>({
    mode: "onBlur",
    disabled: isPendingQueryAccountSubmission,
    resolver: yupResolver(queryAccountSubmissionSchema),
  });

  const topicId = watch("topicId");
  const submissionId = watch("submissionId");

  useEffect(() => {
    reset({
      callRequested,
      topicId: presetTopicId,
    });
  }, [presetTopicId, callRequested, reset]);

  const handleSelectTopicId = (value: string) => {
    setValue("topicId", value);
    clearErrors("topicId");
  };

  const handleSelectSubmissionId = (value: string) => {
    setValue("submissionId", value);
    clearErrors("submissionId");
  };

  const handleCancel = () => {
    reset();
    if (onBack) {
      onBack();
    }
  };

  const onSubmit = () => {
    const data = getValues();
    mutateQueryAccountSubmission({ ...data });
  };

  return (
    <Form
      className="flex flex-col px-1 text-black-100"
      onSubmit={handleSubmit(onSubmit)}
    >
      <input
        type="hidden"
        value={category}
        {...register<"category">("category")}
      />
      <FormRow top="none">
        <Dropdown
          items={topics}
          classNameExpandedContainer="overflow-y-auto max-h-52"
          label="Topic"
          placeholder="Select topic..."
          isSimpleLabel
          value={topicId}
          onSelect={handleSelectTopicId}
          error={formState.errors.topicId?.message}
        />
        <input
          {...register<"topicId">("topicId")}
          type="hidden"
          name="topicId"
        />
      </FormRow>
      {category === "submission" && (
        <FormRow top="small">
          <Dropdown
            items={submissions}
            classNameExpandedContainer="overflow-y-auto max-h-52"
            label="Submission"
            placeholder="Select submission..."
            isSimpleLabel
            value={submissionId}
            onSelect={handleSelectSubmissionId}
            error={formState.errors.submissionId?.message}
          />
          <input
            {...register<"submissionId">("submissionId")}
            type="hidden"
            name="submissionId"
          />
        </FormRow>
      )}
      <FormRow top={"small"}>
        <FormInput
          {...register<"subject">("subject")}
          name="subject"
          label="Subject"
          isSimpleLabel
          error={formState.errors.subject?.message}
          onBlur={({ currentTarget }) =>
            setValue("subject", currentTarget.value.trim())
          }
          onChange={() => clearErrors("subject")}
        />
      </FormRow>
      <FormRow top="small">
        <FormText
          {...register<"message">("message")}
          name="message"
          label="Message"
          isSimpleLabel
          placeholder="What happened? What were you trying to do?"
          error={formState.errors.message?.message}
          onChange={() => clearErrors("message")}
        />
      </FormRow>
      <FormRow top="small">
        <div className="text-black-54">
          New queries are raised as <strong>Clarification</strong> type by
          default. Your accountant can update the type if needed.
        </div>
      </FormRow>
      <FormRow top="small">
        <FormCheckbox
          {...register<"callRequested">("callRequested")}
          name="callRequested"
          label="Request a call — I'd like to discuss this on a call"
          className="*:text-200 *:font-normal"
          error={formState.errors.callRequested?.message}
          onChange={() => clearErrors("callRequested")}
        />
      </FormRow>
      <div className="flex flex-col gap-y-2 py-6 md:flex-row md:justify-between">
        <Button type="submit" title="Send Query" height="small" />
        {onBack && (
          <Button
            variant="outline"
            title="Back"
            height="small"
            onClick={handleCancel}
          />
        )}
      </div>
    </Form>
  );
};
